import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';

interface Blip {
  id: number;
  angle: number;
  radius: number;
  color: string;
  pulsePhase: number;
  priority: string;
}

interface RadarProps {
  size?: number;
  sweepSpeed?: number; // milliseconds for full rotation
  incidents?: Array<{
    id: number;
    priority: string;
    status: string;
  }>;
  syncWithClock?: boolean; // Sync radar sweep with clock seconds
  minimal?: boolean; // Hide label and count (for header use)
}

const Radar: React.FC<RadarProps> = ({ 
  size = 200, 
  sweepSpeed = 10000,
  incidents = [],
  syncWithClock = false,
  minimal = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const startTimeRef = useRef<number>(Date.now());
  const [blips, setBlips] = useState<Blip[]>([]);
  const [currentSecond, setCurrentSecond] = useState(new Date().getSeconds());

  // Generate blips from incidents
  useEffect(() => {
    const activeIncidents = incidents.filter(inc => 
      inc.status?.toLowerCase() !== 'closed'
    );
    
    const newBlips: Blip[] = activeIncidents.map((inc, index) => {
      // Generate pseudo-random but consistent position based on incident id
      const seed = inc.id * 9301 + 49297;
      const angle = ((seed % 360) * Math.PI) / 180;
      const radius = 0.3 + ((seed % 50) / 100); // 0.3 to 0.8 of max radius
      
      let color = '#00FF41'; // Default green for low
      const priority = inc.priority?.toLowerCase() || 'low';
      
      if (priority === 'critical') {
        color = '#DC143C'; // Red
      } else if (priority === 'high' || priority === 'medium') {
        color = '#FFA500'; // Amber
      }
      
      return {
        id: inc.id,
        angle,
        radius,
        color,
        pulsePhase: (index * 0.5) % (Math.PI * 2),
        priority
      };
    });
    
    setBlips(newBlips);
  }, [incidents]);

  // Update current second for clock synchronization
  useEffect(() => {
    if (!syncWithClock) return;
    
    const updateSecond = () => {
      setCurrentSecond(new Date().getSeconds());
    };
    
    const interval = setInterval(updateSecond, 1000);
    updateSecond(); // Initial update
    
    return () => clearInterval(interval);
  }, [syncWithClock]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const centerX = size / 2;
    const centerY = size / 2;
    const maxRadius = (size / 2) - 10;
    
    // Calculate sweep angle based on time or clock synchronization
    let sweepAngle: number;
    if (syncWithClock) {
      // Sync with clock: 360 degrees / 60 seconds = 6 degrees per second
      // Start from top (-90 degrees / -Math.PI/2)
      const seconds = new Date().getSeconds();
      const milliseconds = new Date().getMilliseconds();
      const totalSeconds = seconds + milliseconds / 1000;
      sweepAngle = (totalSeconds / 60) * Math.PI * 2 - Math.PI / 2;
    } else {
      const elapsed = Date.now() - startTimeRef.current;
      sweepAngle = ((elapsed % sweepSpeed) / sweepSpeed) * Math.PI * 2 - Math.PI / 2;
    }
    
    // Clear canvas with fade effect for trail
    ctx.fillStyle = 'rgba(15, 20, 25, 0.15)';
    ctx.fillRect(0, 0, size, size);
    
    // Draw background (only on first few frames for performance)
    if (!syncWithClock) {
      const elapsed = Date.now() - startTimeRef.current;
      if (elapsed < 100) {
        ctx.fillStyle = '#0A0F14';
        ctx.fillRect(0, 0, size, size);
      }
    }
    
    // Draw polar grid - concentric circles
    ctx.strokeStyle = 'rgba(0, 255, 65, 0.15)';
    ctx.lineWidth = 1;
    
    for (let i = 1; i <= 4; i++) {
      ctx.beginPath();
      ctx.arc(centerX, centerY, (maxRadius / 4) * i, 0, Math.PI * 2);
      ctx.stroke();
    }
    
    // Draw radial lines (every 30 degrees)
    for (let i = 0; i < 12; i++) {
      const angle = (i * 30 * Math.PI) / 180;
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(
        centerX + Math.cos(angle) * maxRadius,
        centerY + Math.sin(angle) * maxRadius
      );
      ctx.stroke();
    }
    
    // Draw sweep line with glow
    ctx.save();
    
    // Glow effect
    ctx.shadowColor = '#00FF41';
    ctx.shadowBlur = 20;
    
    // Main sweep line
    ctx.strokeStyle = '#00FF41';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(
      centerX + Math.cos(sweepAngle) * maxRadius,
      centerY + Math.sin(sweepAngle) * maxRadius
    );
    ctx.stroke();
    
    // Draw sweep gradient trail
    const gradient = ctx.createConicGradient(sweepAngle, centerX, centerY);
    gradient.addColorStop(0, 'rgba(0, 255, 65, 0.3)');
    gradient.addColorStop(0.1, 'rgba(0, 255, 65, 0.1)');
    gradient.addColorStop(0.2, 'rgba(0, 255, 65, 0)');
    gradient.addColorStop(1, 'rgba(0, 255, 65, 0)');
    
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, maxRadius, sweepAngle - 0.5, sweepAngle);
    ctx.closePath();
    ctx.fill();
    
    ctx.restore();
    
    // Draw blips
    const currentTime = Date.now() / 1000;
    
    blips.forEach(blip => {
      const blipX = centerX + Math.cos(blip.angle) * (blip.radius * maxRadius);
      const blipY = centerY + Math.sin(blip.angle) * (blip.radius * maxRadius);
      
      // Pulse effect
      const pulse = 1 + Math.sin(currentTime * 3 + blip.pulsePhase) * 0.3;
      const blipSize = 4 * pulse;
      
      // Check if sweep line just passed this blip (within last 2 seconds of sweep)
      const blipAngleNormalized = ((blip.angle + Math.PI / 2) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
      const sweepAngleNormalized = ((sweepAngle + Math.PI / 2) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
      const angleDiff = (sweepAngleNormalized - blipAngleNormalized + Math.PI * 2) % (Math.PI * 2);
      const brightness = angleDiff < 0.5 ? 1 : Math.max(0.3, 1 - (angleDiff / Math.PI));
      
      ctx.save();
      
      // Glow effect for blip
      ctx.shadowColor = blip.color;
      ctx.shadowBlur = 10 * brightness;
      
      // Draw blip
      ctx.fillStyle = blip.color;
      ctx.globalAlpha = brightness;
      ctx.beginPath();
      ctx.arc(blipX, blipY, blipSize, 0, Math.PI * 2);
      ctx.fill();
      
      // Inner bright spot
      ctx.fillStyle = '#FFFFFF';
      ctx.globalAlpha = brightness * 0.8;
      ctx.beginPath();
      ctx.arc(blipX, blipY, blipSize * 0.4, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.restore();
    });
    
    // Draw center point
    ctx.fillStyle = '#00FF41';
    ctx.beginPath();
    ctx.arc(centerX, centerY, 3, 0, Math.PI * 2);
    ctx.fill();
    
    // Continue animation
    animationRef.current = requestAnimationFrame(draw);
  }, [size, sweepSpeed, blips, syncWithClock, currentSecond]);

  useEffect(() => {
    startTimeRef.current = Date.now();
    animationRef.current = requestAnimationFrame(draw);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [draw]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: '50%',
        overflow: 'hidden',
        border: '2px solid rgba(0, 255, 65, 0.3)',
        boxShadow: '0 0 30px rgba(0, 255, 65, 0.2), inset 0 0 30px rgba(0, 0, 0, 0.5)',
      }}
    >
      <canvas
        ref={canvasRef}
        width={size}
        height={size}
        style={{
          display: 'block',
          background: '#0A0F14',
        }}
      />
      
      {/* Radar label - hidden in minimal mode */}
      {!minimal && (
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            fontFamily: "'JetBrains Mono', 'SF Mono', monospace",
            fontSize: '0.6rem',
            color: 'rgba(0, 255, 65, 0.6)',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
          }}
        >
          ATC RADAR
        </Typography>
      )}
      
      {/* Incident count indicator - hidden in minimal mode */}
      {!minimal && blips.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: 1,
            background: 'rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(0, 255, 65, 0.3)',
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: '#00FF41',
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.5 },
              },
            }}
          />
          <Typography
            sx={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '0.65rem',
              color: '#00FF41',
            }}
          >
            {blips.length}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Radar;
